import React, { useState, useEffect, useRef } from 'react';
import { t, tList } from '../i18n';
import { G2FaceMesh, G2_PTS } from './glyphs';
import type { SelfieSource } from './Stage03SelfieCapture';
import {
  centroid,
  computeCanonicalEyeTargets,
  imagePointToCoverBox,
  eyeCentersToSimilarityTransform,
  similarityTransformToCss,
  selectAlignment,
  guideRectHorizontalExtremes,
  canonicalOvalExtremes,
  VC_STAGE,
  G2_VIEWBOX,
  G2_MESH_PLACEMENT,
} from '../lib/faceAlign';
import type { GuideRect, Point } from '../lib/faceAlign';
import { detectEyes, disposeDetector } from '../lib/faceDetect';
import { resolveModelAssetBase } from '../lib/assetBase';

const PHOTO_OVAL_RX = 98;

function toPoints(pts: Array<[number, number]>): Point[] {
  return pts.map(([x, y]) => ({ x, y }));
}

// One pass per layer; durations follow --dur-scan-pass (4200 ms).
const SCAN_PASS_MS = 4200;

// 4a honesty framing: this beat is *preparation* on-device, NOT the verdict.
// Copy must never claim the report/results are ready — the genuine analysis
// runs server-side after submit and is delivered by email. Title/narration copy
// is resolved from the i18n catalog (keys below) so it follows the clinic locale.
const VC_PHASES = [
  {
    key: 'surface',
    layers: ['live', '', ''],
    titlePreKey: 'scan.p0.titlePre',
    titleEmKey: 'scan.p0.titleEm',
    narrationKey: 'scan.p0.narration',
  },
  {
    key: 'texture',
    layers: ['done', 'live', ''],
    titlePreKey: 'scan.p1.titlePre',
    titleEmKey: 'scan.p1.titleEm',
    narrationKey: 'scan.p1.narration',
  },
  {
    key: 'detail',
    layers: ['done', 'done', 'live'],
    titlePreKey: 'scan.p2.titlePre',
    titleEmKey: 'scan.p2.titleEm',
    narrationKey: 'scan.p2.narration',
  },
] as const;

function prefersReducedMotion(): boolean {
  return typeof window.matchMedia === 'function'
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

interface StageScanPreviewProps {
  selfiePhoto: File | null;
  selfieSource: SelfieSource | null;
  guideRect?: GuideRect;
  /** Advance to the contact step once the preparation passes complete. */
  onComplete: () => void;
  /** Report the resolved alignment transform so later stages can reuse it. */
  onAligned?: (transform: string | undefined) => void;
}

export const StageScanPreview = ({
  selfiePhoto,
  selfieSource,
  guideRect,
  onComplete,
  onAligned,
}: StageScanPreviewProps) => {
  const [phase, setPhase] = useState(0);
  const reducedMotionRef = useRef(prefersReducedMotion());
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  const [photoTransform, setPhotoTransform] = useState<string | undefined>(undefined);

  // Build the bitmap (EXIF-aware), run on-device cosmetic detection, and resolve
  // the alignment transform. This is the real local work the preview reflects.
  // Fully non-blocking; every failure degrades down the fallback ladder.
  useEffect(() => {
    if (!selfiePhoto) return;

    const url = URL.createObjectURL(selfiePhoto);
    setPhotoUrl(url);

    let cancelled = false;
    let bitmap: ImageBitmap | null = null;

    const eyeLvb = centroid(toPoints(G2_PTS.eyeL));
    const eyeRvb = centroid(toPoints(G2_PTS.eyeR));
    const targets = computeCanonicalEyeTargets(
      VC_STAGE.w, VC_STAGE.h, G2_MESH_PLACEMENT, G2_VIEWBOX, eyeLvb, eyeRvb,
    );

    void (async () => {
      try {
        bitmap = await createImageBitmap(selfiePhoto, { imageOrientation: 'from-image' });
      } catch {
        bitmap = null;
      }

      let detected: { left: Point; right: Point } | null = null;
      if (bitmap) {
        try {
          detected = await detectEyes(bitmap, resolveModelAssetBase());
        } catch {
          detected = null;
        }
      }
      if (cancelled) return;

      const rung = selectAlignment({
        hasPhoto: true,
        source: selfieSource,
        detected: detected != null,
        modelOk: detected != null,
        hasGuideRect: guideRect != null,
      });

      let resolved: string | undefined;
      if (rung === 'similarity' && detected && bitmap) {
        const boxL = imagePointToCoverBox(detected.left, bitmap.width, bitmap.height, VC_STAGE.w, VC_STAGE.h);
        const boxR = imagePointToCoverBox(detected.right, bitmap.width, bitmap.height, VC_STAGE.w, VC_STAGE.h);
        resolved = similarityTransformToCss(
          eyeCentersToSimilarityTransform(boxL, boxR, targets.left, targets.right),
        );
      } else if (rung === 'guide-crop' && guideRect && bitmap) {
        const ext = guideRectHorizontalExtremes(guideRect);
        const boxL = imagePointToCoverBox(ext.left, bitmap.width, bitmap.height, VC_STAGE.w, VC_STAGE.h);
        const boxR = imagePointToCoverBox(ext.right, bitmap.width, bitmap.height, VC_STAGE.w, VC_STAGE.h);
        const canon = canonicalOvalExtremes(VC_STAGE.w, VC_STAGE.h, PHOTO_OVAL_RX);
        resolved = similarityTransformToCss(
          eyeCentersToSimilarityTransform(boxL, boxR, canon.left, canon.right),
        );
      } else {
        resolved = undefined;
      }
      setPhotoTransform(resolved);
      onAligned?.(resolved);
    })();

    return () => {
      cancelled = true;
      URL.revokeObjectURL(url);
      bitmap?.close();
      disposeDetector();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Advance the chamber one pass per layer; hold on the last pass.
  useEffect(() => {
    if (reducedMotionRef.current) return;
    const interval = setInterval(() => {
      setPhase((p) => Math.min(p + 1, VC_PHASES.length - 1));
    }, SCAN_PASS_MS);
    return () => clearInterval(interval);
  }, []);

  // Auto-advance to the contact step once the full preparation sequence plays
  // (full SURFACE→TEXTURE→SUBSURFACE; with reduced motion, a 2 s static floor).
  useEffect(() => {
    const holdMs = reducedMotionRef.current ? 2000 : SCAN_PASS_MS * VC_PHASES.length;
    const timer = setTimeout(() => onComplete(), holdMs);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reduced = reducedMotionRef.current;
  const current = VC_PHASES[reduced ? VC_PHASES.length - 1 : phase];
  const layerStates = reduced ? (['done', 'done', 'done'] as const) : current.layers;

  const photoStyle: React.CSSProperties | undefined = photoTransform
    ? { transform: photoTransform, transformOrigin: '0 0' }
    : undefined;

  return (
    <div className="vc-frame stage-04-processing">
      <div className="vc-head">
        <span className="vc-head-btn vc-head-btn--ghost" aria-hidden="true"></span>
        <span className="vc-steplabel">{t('scan.step')}</span>
        <span className="vc-head-btn vc-head-btn--ghost" aria-hidden="true"></span>
      </div>

      <div className="vc-stage" aria-hidden="true">
        {selfiePhoto && photoUrl && (
          <div className="vc-photo-stack" aria-hidden="true">
            <div className="vc-photo-frame">
              <img className="vc-photo" src={photoUrl} alt="" style={photoStyle} />
            </div>
            <div className="vc-photo-tint-lo"></div>
            <div className="vc-photo-tint-hi"></div>
            {!reduced && (
              <div className="vc-photo-frame">
                <img key={phase} className="vc-photo-reveal" src={photoUrl} alt="" style={photoStyle} />
              </div>
            )}
          </div>
        )}
        <div className="vc-halo"></div>
        <div className="vc-depth"><i></i><i></i><i></i></div>
        <G2FaceMesh
          className="mesh"
          stroke="var(--mesh-stroke)"
          dot="var(--mesh-dot)"
          strokeWidth={0.75}
          dotR={1.7}
          meshOpacity={reduced ? 0.59 : 0.35 + phase * 0.12}
          dotOpacity={reduced ? 1 : 0.7 + phase * 0.15}
        />
        {!reduced && <div key={phase} className="vc-scanpass vc-scanpass--sweep"></div>}
      </div>

      <div className="vc-layers" style={{ marginTop: 'var(--sp-7)' }} aria-hidden="true">
        {tList('scan.layers').map((label, i) => (
          <span
            key={label}
            className={`vc-layer ${layerStates[i] === 'live' ? 'is-live' : layerStates[i] === 'done' ? 'is-done' : ''}`}
          >
            {label}
          </span>
        ))}
      </div>

      <h2 className="vc-title" role="heading" aria-level={2}>
        <span>{t(current.titlePreKey)}<em>{t(current.titleEmKey)}</em></span>
      </h2>
      <p className="vc-narration" aria-live="polite" aria-atomic="true">{t(current.narrationKey)}</p>

      <p className="vc-chamber-foot">
        {t('scan.foot')}
      </p>

      <div aria-live="polite" aria-atomic="true" className="visually-hidden">
        {t('scan.live')}
      </div>
    </div>
  );
};
